'use client';

import {
  Alert,
  AlertProps,
  AlertDescription,
  Box,
  IconButton,
  createMultiStyleConfigHelpers,
  forwardRef
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { alertAnatomy } from '@chakra-ui/anatomy';

import Icon, { IconName } from '@/components/atomic/v1/icon/Icon';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(alertAnatomy.keys);

const baseStyle = definePartsStyle({
  container: {
    p: { base: 4, md: 6 },
    borderBottom: '2px solid',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 3
  }
});

const successVariant = definePartsStyle({
  container: {
    bg: 'status/success/bg',
    borderBottomColor: 'status/success/default'
  }
});
const errorVariant = definePartsStyle({
  container: {
    bg: 'status/error/bg',
    borderBottomColor: 'status/error/default'
  }
});
const warningVariant = definePartsStyle({
  container: {
    bg: 'status/warning/bg',
    borderBottomColor: 'status/warning/default'
  }
});
const infoVariant = definePartsStyle({
  container: {
    bg: 'status/information/bg',
    borderBottomColor: 'status/information/default'
  }
});

export const calloutTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {
    success: successVariant,
    error: errorVariant,
    warning: warningVariant,
    info: infoVariant
  }
});

export type CalloutVariant = 'success' | 'error' | 'warning' | 'info';

export interface CalloutProps extends AlertProps {
  variant: CalloutVariant;
  children: ReactNode;
  onClose?: () => void;
}

/**
 * Based on `<Alert>` from ChakraUI component library
 *
 * https://chakra-ui.com/docs/components/alert
 */
const Callout = forwardRef<CalloutProps, 'div'>(({ children, variant, onClose, ...rest }, ref) => {
  const iconRecord: Record<CalloutVariant, [IconName, string]> = {
    success: ['checkmark-circle', 'status/success/default'],
    error: ['warning', 'status/error/default'],
    warning: ['warning', 'status/warning/default'],
    info: ['info', 'status/information/default']
  };

  const [iconName, iconColor] = iconRecord[variant];

  return (
    <Alert variant={variant} ref={ref} {...rest}>
      <Box>
        <Icon name={iconName} kind="solid" color={iconColor} />
      </Box>

      <AlertDescription flex={1} textColor="text/body">
        {children}
      </AlertDescription>

      {onClose && (
        <IconButton
          aria-label="Close callout"
          variant="unstyled"
          size=""
          icon={<Icon name="close" color="icon/default" />}
          onClick={onClose}
        />
      )}
    </Alert>
  );
});

export default Callout;
